import { Link } from "react-router-dom";
import { formatUnits } from "viem";
import { TrendingUp, Users, BarChart3 } from "lucide-react";
import { type MemeToken, TokenStatus, tokenStatusLabel, tokenStatusColor } from "../../config/tokens";
import { formatTokenAmount, timeAgo, shortenAddress, cn } from "../../lib/utils";
import { useLaunchpadConfig } from "../../hooks/useLaunchpad";

interface TokenCardProps {
  token: MemeToken;
}

export function TokenCard({ token }: TokenCardProps) {
  const { graduationThreshold } = useLaunchpadConfig();

  const progress = token.status === TokenStatus.Graduated
    ? 100
    : graduationThreshold > 0n
      ? Math.min(Number((token.reserveBalance * 10000n) / graduationThreshold) / 100, 100)
      : 0;

  return (
    <Link
      to={`/launchpad/token/${token.tokenAddress}`}
      className="glass-card-hover p-4 block group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-card overflow-hidden bg-surface flex-shrink-0">
          {token.imageUri ? (
            <img
              src={token.imageUri}
              alt={token.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
                (e.target as HTMLImageElement).parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center text-lg font-bold text-primary bg-primary/10">${token.symbol.slice(0, 2)}</div>`;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-lg font-bold text-primary bg-primary/10">
              {token.symbol.slice(0, 2)}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-text-primary truncate group-hover:text-primary transition-colors">
              {token.name}
            </h3>
            <span
              className={cn(
                "text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                token.status === TokenStatus.Live
                  ? "bg-primary/10 text-primary border-primary/20"
                  : token.status === TokenStatus.Graduated
                  ? "bg-secondary/10 text-secondary border-secondary/20"
                  : "bg-error/10 text-error border-error/20"
              )}
            >
              {tokenStatusLabel(token.status)}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-text-tertiary">${token.symbol}</span>
            <span className="text-[10px] text-text-tertiary">
              by {shortenAddress(token.creator)}
            </span>
          </div>
        </div>
      </div>

      {token.description && (
        <p className="text-xs text-text-tertiary line-clamp-2 mb-3">
          {token.description}
        </p>
      )}

      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-text-tertiary">Bonding Curve</span>
          <span className="text-text-secondary font-medium">{progress}%</span>
        </div>
        <div className="w-full h-1.5 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg, #00d4aa, #7b61ff)`,
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="flex items-center gap-1 text-text-tertiary">
          <TrendingUp className="w-3 h-3" />
          <span>{formatTokenAmount(token.reserveBalance)} JASMY</span>
        </div>
        <div className="flex items-center gap-1 text-text-tertiary">
          <Users className="w-3 h-3" />
          <span>{Number(token.holderCount)}</span>
        </div>
        <div className="flex items-center gap-1 text-text-tertiary">
          <BarChart3 className="w-3 h-3" />
          <span>{Number(token.tradeCount)} trades</span>
        </div>
      </div>

      <div className="text-[10px] text-text-tertiary mt-2">
        {timeAgo(token.createdAt)}
      </div>
    </Link>
  );
}
